import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    RefreshCw, MousePointerClick, Flame, MousePointer2, ArrowDownWideNarrow,
    Eye, Users, Clock, Monitor, Smartphone, LayoutGrid, HelpCircle,
} from 'lucide-react';
import { fetchHeatmapPages, fetchHeatmapClicks, fetchHeatmapScreenshot } from '../api/analyticsApi';

/* ── helpers ──────────────────────────────────────────────────────────────── */
const fmtInt = (n) => (Number(n) || 0).toLocaleString();
const fmtDuration = (ms) => {
    const s = Math.round((Number(ms) || 0) / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return `${m}m ${rem}s`;
};

/* ── Heatmap colour palette (blue → cyan → green → yellow → red) ───────────── */
const buildPalette = () => {
    // 256-entry RGB ramp, indexed by intensity 0..255.
    const stops = [
        [0, [0, 0, 255]],
        [64, [0, 255, 255]],
        [128, [0, 255, 0]],
        [192, [255, 255, 0]],
        [255, [255, 0, 0]],
    ];
    const palette = new Array(256);
    for (let i = 0; i < 256; i += 1) {
        let lo = stops[0];
        let hi = stops[stops.length - 1];
        for (let s = 0; s < stops.length - 1; s += 1) {
            if (i >= stops[s][0] && i <= stops[s + 1][0]) {
                lo = stops[s];
                hi = stops[s + 1];
                break;
            }
        }
        const span = hi[0] - lo[0] || 1;
        const t = (i - lo[0]) / span;
        palette[i] = [
            Math.round(lo[1][0] + (hi[1][0] - lo[1][0]) * t),
            Math.round(lo[1][1] + (hi[1][1] - lo[1][1]) * t),
            Math.round(lo[1][2] + (hi[1][2] - lo[1][2]) * t),
        ];
    }
    return palette;
};
const PALETTE = buildPalette();

/* ── Canvas click heatmap ─────────────────────────────────────────────────── */
function ClickHeatmap({ points, maxWeight, pageAspect, path }) {
    const wrapRef = useRef(null);
    const canvasRef = useRef(null);
    const [width, setWidth] = useState(640);
    const [showPage, setShowPage] = useState(true);
    // Backdrop screenshot state: { status, url, aspect } from the API.
    const [shot, setShot] = useState(null);
    const [shotLoading, setShotLoading] = useState(false);

    // Fetch the page screenshot for this screen; poll while it's being captured.
    useEffect(() => {
        if (!path) { setShot(null); return undefined; }
        let cancelled = false;
        let timer = null;
        setShot(null);
        setShotLoading(true);

        let attempts = 0;
        const MAX_ATTEMPTS = 20; // ~80s at 4s intervals, then give up gracefully
        const poll = async () => {
            try {
                const data = await fetchHeatmapScreenshot(path);
                if (cancelled) return;
                setShot(data);
                attempts += 1;
                if (data?.status === 'pending' && attempts < MAX_ATTEMPTS) {
                    timer = setTimeout(poll, 4000); // capture runs in the background
                } else {
                    // ready / disabled / failed, or we've waited long enough.
                    setShotLoading(false);
                }
            } catch (e) {
                if (cancelled) return;
                setShot({ status: 'failed', error: e?.message });
                setShotLoading(false);
            }
        };
        poll();
        return () => { cancelled = true; if (timer) clearTimeout(timer); };
    }, [path]);

    // Track container width so the canvas is responsive.
    useEffect(() => {
        if (!wrapRef.current) return undefined;
        const el = wrapRef.current;
        const ro = new ResizeObserver(() => setWidth(el.clientWidth || 640));
        ro.observe(el);
        setWidth(el.clientWidth || 640);
        return () => ro.disconnect();
    }, []);

    const ready = shot?.status === 'ready' && shot?.url;
    // Prefer the screenshot's own aspect so the heat lines up with the image
    // exactly; fall back to the data-derived page aspect.
    const aspectSrc = ready && shot.aspect ? shot.aspect : pageAspect;
    const aspect = Math.max(0.4, Math.min(8, Number(aspectSrc) || 1.6));
    const height = Math.round(width * aspect);
    const pageVisible = showPage && ready;

    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const W = width;
        const H = height;
        canvas.width = W;
        canvas.height = H;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, W, H);

        if (!points || points.length === 0) return;
        const max = Math.max(1, Number(maxWeight) || 1);

        // 1. Paint grayscale alpha blobs onto an offscreen "intensity" canvas.
        const off = document.createElement('canvas');
        off.width = W;
        off.height = H;
        const octx = off.getContext('2d');
        const radius = Math.max(16, Math.round(Math.min(W, H) * 0.045));

        points.forEach((p) => {
            const px = p.x * W;
            const py = p.y * H;
            const intensity = Math.min(1, 0.15 + 0.85 * ((p.w || 1) / max));
            const grad = octx.createRadialGradient(px, py, 0, px, py, radius);
            grad.addColorStop(0, `rgba(0,0,0,${intensity})`);
            grad.addColorStop(1, 'rgba(0,0,0,0)');
            octx.fillStyle = grad;
            octx.beginPath();
            octx.arc(px, py, radius, 0, Math.PI * 2);
            octx.fill();
        });

        // 2. Colourise by the accumulated alpha channel.
        const img = octx.getImageData(0, 0, W, H);
        const data = img.data;
        for (let i = 0; i < data.length; i += 4) {
            const alpha = data[i + 3];
            if (alpha === 0) continue;
            const color = PALETTE[alpha];
            data[i] = color[0];
            data[i + 1] = color[1];
            data[i + 2] = color[2];
            // Slightly boost low-alpha edges so faint clicks remain visible.
            data[i + 3] = Math.min(255, Math.round(alpha * 0.85) + 40);
        }
        ctx.putImageData(img, 0, 0);
    }, [points, maxWeight, width, height]);

    useEffect(() => { draw(); }, [draw]);

    return (
        <div ref={wrapRef} className="relative w-full">
            {/* Toggle the real-page background on/off (once captured). */}
            {ready && (
                <button
                    onClick={() => setShowPage((v) => !v)}
                    className={`absolute top-2 right-2 z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold shadow-sm border transition-all ${
                        pageVisible
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                    }`}
                    title="Show the actual page behind the heatmap"
                >
                    <Eye className="w-3 h-3" />
                    {pageVisible ? 'Page on' : 'Page off'}
                </button>
            )}

            {/* Neutral "page" backdrop with scroll-fold guides for spatial context. */}
            <div
                className="relative w-full rounded-lg overflow-hidden border border-gray-200 bg-gradient-to-b from-gray-50 to-gray-100"
                style={{ height }}
            >
                {/* Prominent loading overlay while the backdrop screenshot for this
                    screen is being fetched / captured (shows instantly on switch). */}
                {shotLoading && (
                    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-white/70 backdrop-blur-sm">
                        <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
                        <span className="text-sm font-semibold text-gray-600">
                            {shot?.status === 'pending' ? 'Capturing page…' : 'Loading heatmap…'}
                        </span>
                    </div>
                )}
                {/* Cached full-page screenshot of the real portal page, behind the
                    heat. Both are normalised to the full page, so they line up. */}
                {pageVisible && (
                    <img
                        src={shot.url}
                        alt="Page background"
                        className="absolute inset-0 w-full h-full object-cover object-top pointer-events-none select-none"
                        draggable={false}
                    />
                )}

                {/* Viewport-fold lines (hidden when the real page is shown). */}
                {!pageVisible && Array.from({ length: Math.floor(aspect) }).map((_, i) => (
                    <div
                        key={i}
                        className="absolute left-0 right-0 border-t border-dashed border-gray-300"
                        style={{ top: `${((i + 1) / aspect) * 100}%` }}
                    >
                        <span className="absolute -top-2 right-1 text-[10px] text-gray-400 bg-gray-100 px-1 rounded">
                            fold {i + 1}
                        </span>
                    </div>
                ))}
                <canvas
                    ref={canvasRef}
                    className="absolute inset-0 w-full h-full pointer-events-none"
                    style={{ opacity: pageVisible ? 0.72 : 1 }}
                />
            </div>
            <div className="flex items-center justify-between mt-2 text-[11px] text-gray-400">
                <span>Top of page</span>
                <span className="flex items-center gap-1">
                    Cold
                    <span className="inline-block w-24 h-2 rounded-full" style={{
                        background: 'linear-gradient(to right, #0000ff, #00ffff, #00ff00, #ffff00, #ff0000)',
                    }} />
                    Hot
                </span>
                <span>Bottom of page</span>
            </div>
            {pageVisible && (path?.includes(':id') || path?.includes(':token')) && (
                <div className="mt-1 text-[10px] text-gray-400">
                    Backdrop is a representative page for this screen — heat is aggregated across all of them.
                </div>
            )}
        </div>
    );
}

/* ── Stat chip ─────────────────────────────────────────────────────────────── */
const Chip = ({ Icon, label, value, accent = '#3B82F6' }) => (
    <div className="flex items-center gap-2.5 bg-white rounded-xl border border-gray-200 px-3 py-2.5">
        <span
            className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0"
            style={{ background: `${accent}1a`, color: accent }}
        >
            <Icon className="w-4 h-4" />
        </span>
        <div className="min-w-0">
            <div className="text-lg font-bold text-gray-900 leading-tight">{value}</div>
            <div className="text-[11px] text-gray-500">{label}</div>
        </div>
    </div>
);

/* ── Device toggle ─────────────────────────────────────────────────────────── */
const DeviceToggle = ({ device, onChange }) => {
    const opts = [
        { key: 'all', label: 'All', Icon: LayoutGrid },
        { key: 'desktop', label: 'Desktop', Icon: Monitor },
        { key: 'mobile', label: 'Mobile', Icon: Smartphone },
    ];
    return (
        <div className="inline-flex bg-gray-100 p-1 rounded-lg">
            {opts.map(({ key, label, Icon }) => (
                <button
                    key={key}
                    onClick={() => onChange(key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                        device === key ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                    <Icon className="w-3.5 h-3.5" />
                    {label}
                </button>
            ))}
        </div>
    );
};

/* ── Main panel ───────────────────────────────────────────────────────────── */
export default function HeatmapPanel({ startDate, endDate }) {
    const [device, setDevice] = useState('all');
    const [pagesData, setPagesData] = useState(null);
    const [pagesLoading, setPagesLoading] = useState(true);
    const [pagesError, setPagesError] = useState(null);

    const [selectedPath, setSelectedPath] = useState(null);
    const [clicks, setClicks] = useState(null);
    const [clicksLoading, setClicksLoading] = useState(false);
    const [clicksError, setClicksError] = useState(null);

    // Load the most-visited pages ranking.
    useEffect(() => {
        let cancelled = false;
        setPagesLoading(true);
        setPagesError(null);
        fetchHeatmapPages(startDate, endDate, device)
            .then((data) => {
                if (cancelled) return;
                setPagesData(data);
                // Auto-select the busiest page if nothing chosen (or prior choice gone).
                const paths = (data?.pages || []).map((p) => p.path);
                setSelectedPath((prev) => (prev && paths.includes(prev) ? prev : paths[0] || null));
            })
            .catch((e) => { if (!cancelled) setPagesError(e?.message || 'Failed to load'); })
            .finally(() => { if (!cancelled) setPagesLoading(false); });
        return () => { cancelled = true; };
    }, [startDate, endDate, device]);

    // Load the click heatmap for the selected page.
    useEffect(() => {
        if (!selectedPath) { setClicks(null); return undefined; }
        let cancelled = false;
        setClicksLoading(true);
        setClicksError(null);
        fetchHeatmapClicks(selectedPath, startDate, endDate, device)
            .then((data) => { if (!cancelled) setClicks(data); })
            .catch((e) => { if (!cancelled) setClicksError(e?.message || 'Failed to load'); })
            .finally(() => { if (!cancelled) setClicksLoading(false); });
        return () => { cancelled = true; };
    }, [selectedPath, startDate, endDate, device]);

    const pages = pagesData?.pages || [];
    const maxViews = Math.max(1, ...pages.map((p) => p.views || 0));

    return (
        <div>
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                <div>
                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <Flame className="w-5 h-5 text-orange-500" />
                        Screen Heatmap
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Where buyers go and what they click on the portal. Pick a screen to see its click heatmap,
                        most-clicked elements, scroll depth and frustration signals.
                    </p>
                </div>
                <DeviceToggle device={device} onChange={setDevice} />
            </div>

            {/* Top-line totals */}
            {pagesData && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
                    <Chip Icon={Eye} label="Total page views" value={fmtInt(pagesData.total_views)} accent="#3B82F6" />
                    <Chip Icon={Users} label="Unique visitors" value={fmtInt(pagesData.total_visitors)} accent="#10B981" />
                    <Chip Icon={LayoutGrid} label="Screens tracked" value={fmtInt(pagesData.page_count)} accent="#8B5CF6" />
                </div>
            )}

            {pagesData?.capped && (
                <div className="mb-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    Showing the {fmtInt(pagesData.event_cap)} most recent events for this range — narrow the date
                    range for exact totals on very busy periods.
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* ── Most-visited screens ─────────────────────────────────── */}
                <div className="lg:col-span-1">
                    <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                        <Eye className="w-4 h-4 text-gray-400" />
                        Most Visited Screens
                    </h3>
                    {pagesLoading ? (
                        <div className="flex items-center gap-2 text-sm text-gray-400 py-8 justify-center">
                            <RefreshCw className="w-4 h-4 animate-spin" /> Loading…
                        </div>
                    ) : pagesError ? (
                        <div className="text-sm text-red-500 py-6 text-center">{pagesError}</div>
                    ) : pages.length === 0 ? (
                        <div className="text-sm text-gray-400 py-8 text-center">
                            No screen-tracking data yet for this range.
                        </div>
                    ) : (
                        <div className="space-y-1.5 max-h-[640px] overflow-y-auto pr-1">
                            {pages.map((p) => (
                                <button
                                    key={p.path}
                                    onClick={() => setSelectedPath(p.path)}
                                    className={`w-full text-left rounded-lg border px-3 py-2.5 transition-all ${
                                        selectedPath === p.path
                                            ? 'border-blue-400 bg-blue-50 ring-1 ring-blue-200'
                                            : 'border-gray-200 bg-white hover:border-gray-300'
                                    }`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-sm font-semibold text-gray-800 truncate" title={p.label}>
                                            {p.label}
                                        </span>
                                        <span className="text-xs font-bold text-gray-700 flex-shrink-0">
                                            {fmtInt(p.views)} views
                                        </span>
                                    </div>
                                    <div className="text-[11px] text-gray-400 truncate" title={p.path}>{p.path}</div>
                                    <div className="mt-1.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                            className="h-1.5 rounded-full bg-blue-500"
                                            style={{ width: `${(p.views / maxViews) * 100}%` }}
                                        />
                                    </div>
                                    <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-500">
                                        <span className="flex items-center gap-1"><Users className="w-3 h-3" />{fmtInt(p.visitors)}</span>
                                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDuration(p.avg_duration_ms)}</span>
                                        <span className="flex items-center gap-1"><ArrowDownWideNarrow className="w-3 h-3" />{p.avg_scroll_pct}%</span>
                                        <span className="flex items-center gap-1"><MousePointerClick className="w-3 h-3" />{fmtInt(p.click_count)}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* ── Selected screen detail ───────────────────────────────── */}
                <div className="lg:col-span-2">
                    {!selectedPath ? (
                        <div className="text-sm text-gray-400 py-16 text-center border border-dashed border-gray-200 rounded-xl">
                            Select a screen to view its heatmap.
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-sm font-bold text-gray-700 truncate">
                                    {clicks?.label || selectedPath}
                                    <span className="ml-2 text-[11px] font-normal text-gray-400">{selectedPath}</span>
                                </h3>
                                {clicksLoading && <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />}
                            </div>

                            {/* Per-screen stat chips */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                                <Chip Icon={MousePointerClick} label="Clicks captured" value={fmtInt(clicks?.total_clicks)} accent="#3B82F6" />
                                <Chip Icon={ArrowDownWideNarrow} label="Avg scroll depth" value={`${clicks?.avg_scroll_pct ?? 0}%`} accent="#10B981" />
                                <Chip Icon={Flame} label="Rage clicks" value={fmtInt(clicks?.rage_count)} accent="#EF4444" />
                                <Chip Icon={MousePointer2} label="Dead clicks" value={fmtInt(clicks?.dead_count)} accent="#F59E0B" />
                            </div>

                            {clicksError ? (
                                <div className="text-sm text-red-500 py-6 text-center">{clicksError}</div>
                            ) : (clicks && clicks.total_clicks === 0) ? (
                                <div className="text-sm text-gray-400 py-12 text-center border border-dashed border-gray-200 rounded-xl">
                                    No clicks recorded on this screen yet for the selected range / device.
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                                    {/* Heatmap canvas */}
                                    <div>
                                        <ClickHeatmap
                                            points={clicks?.points}
                                            maxWeight={clicks?.max_weight}
                                            pageAspect={clicks?.page_aspect}
                                            path={selectedPath}
                                        />
                                    </div>

                                    {/* Top clicked elements */}
                                    <div>
                                        <h4 className="text-xs font-bold text-gray-600 mb-2 flex items-center gap-1.5">
                                            <MousePointerClick className="w-3.5 h-3.5 text-gray-400" />
                                            Most Clicked Elements
                                        </h4>
                                        <div className="space-y-1.5 max-h-[560px] overflow-y-auto pr-1">
                                            {(clicks?.elements || []).map((el, idx) => {
                                                const max = Math.max(1, ...(clicks.elements || []).map((e) => e.count || 0));
                                                return (
                                                    <div key={`${el.selector}-${idx}`} className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                                                        <div className="flex items-center justify-between gap-2">
                                                            <span className="text-xs font-semibold text-gray-800 truncate" title={el.txt || el.selector}>
                                                                {el.txt || el.selector || '—'}
                                                            </span>
                                                            <span className="text-xs font-bold text-gray-700 flex-shrink-0">{fmtInt(el.count)}</span>
                                                        </div>
                                                        <div className="flex items-center gap-1.5 mt-0.5">
                                                            <code className="text-[10px] text-gray-400 truncate flex-1" title={el.selector}>{el.selector}</code>
                                                            {el.rage > 0 && (
                                                                <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 rounded flex-shrink-0">
                                                                    {fmtInt(el.rage)} rage
                                                                </span>
                                                            )}
                                                            {el.dead > 0 && (
                                                                <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 rounded flex-shrink-0">
                                                                    {fmtInt(el.dead)} dead
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="mt-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                            <div
                                                                className="h-1.5 rounded-full bg-blue-500"
                                                                style={{ width: `${(el.count / max) * 100}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {(!clicks?.elements || clicks.elements.length === 0) && !clicksLoading && (
                                                <div className="text-sm text-gray-400 py-6 text-center">No element data.</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="mt-3 text-[11px] text-gray-400 flex items-center gap-1.5">
                                <HelpCircle className="w-3.5 h-3.5" />
                                Heatmap shows {fmtInt(clicks?.total_clicks)} clicks across {fmtInt(clicks?.sessions)} visitors.
                                Coordinates are normalised to the full page, so the layout is approximate. “Rage” = rapid
                                repeated clicks (frustration); “dead” = clicks on non-interactive areas (confusion).
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
